import type { ReturnMetricsAudit } from "../../api/client";
import { fmtCurrency, fmtPercent, fmtMultiple } from "../../utils/format";

// ---------- helpers ----------

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-bark-400 mb-0.5">
        {label}
      </div>
      <div className="text-sm font-semibold text-forest-700 tabular-nums">
        {value}
      </div>
    </div>
  );
}

// ---------- Section A: Exit Price Buildup ----------

export function ExitPriceBuildup({ data }: { data: ReturnMetricsAudit }) {
  const rows: { label: string; value: string; indent?: boolean; bold?: boolean }[] = [
    { label: "Year N EGI", value: fmtCurrency(data.final_year_egi) },
    { label: "Revenue Growth Rate", value: fmtPercent(data.revenue_growth_rate, 1), indent: true },
    { label: "Year N+1 EGI", value: fmtCurrency(data.year_n_plus_1_egi), bold: true },
    { label: "Year N OpEx", value: fmtCurrency(data.final_year_opex) },
    { label: "Expense Growth Rate", value: fmtPercent(data.expense_growth_rate, 1), indent: true },
    { label: "Year N+1 OpEx", value: fmtCurrency(data.year_n_plus_1_opex), bold: true },
    { label: "Year N+1 NOI", value: fmtCurrency(data.year_n_plus_1_noi), bold: true },
    { label: "Exit Cap Rate", value: fmtPercent(data.exit_cap_rate, 2), indent: true },
    { label: "Gross Exit Price", value: fmtCurrency(data.gross_exit_price), bold: true },
    {
      label: `Less: Selling Costs (${fmtPercent(data.selling_costs_pct, 1)})`,
      value: fmtCurrency(-Math.abs(data.selling_costs)),
      indent: true,
    },
    { label: "Net Exit Proceeds", value: fmtCurrency(data.net_exit_proceeds), bold: true },
  ];

  return (
    <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-grove-100 border-b border-grove-300">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
          Exit Price Buildup
        </h3>
      </div>
      <div className="divide-y divide-grove-100">
        {rows.map((r, i) => (
          <div
            key={i}
            className={`flex items-center justify-between px-5 py-2.5 ${
              r.bold ? "bg-grove-50" : ""
            }`}
          >
            <span
              className={`text-[11px] ${
                r.indent ? "pl-4 text-bark-400" : ""
              } ${r.bold ? "font-semibold text-bark-700" : "text-bark-600"}`}
            >
              {r.label}
            </span>
            <span
              className={`text-[11px] tabular-nums ${
                r.bold ? "font-semibold text-bark-800" : "text-bark-600"
              }`}
            >
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Section B: IRR Cash Flow Stream ----------

export function IRRCashFlowStream({ data }: { data: ReturnMetricsAudit }) {
  const unlevered = data.unlevered_cf_stream;
  const levered = data.levered_cf_stream;
  const maxLen = Math.max(unlevered.length, levered.length);

  return (
    <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-grove-100 border-b border-grove-300">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
          IRR Cash Flow Stream
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-grove-50 border-b border-grove-300">
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-bark-400 sticky left-0 bg-grove-50 min-w-[80px]">
                Year
              </th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-bark-400 min-w-[120px]">
                Unlevered Stream
              </th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-bark-400 min-w-[120px]">
                Levered Stream
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxLen }, (_, i) => {
              const isFirst = i === 0;
              const isLast = i === maxLen - 1;
              return (
                <tr
                  key={i}
                  className={`border-b border-grove-100 ${
                    isFirst || isLast ? "bg-grove-50/50" : ""
                  }`}
                >
                  <td className="px-4 py-2 text-[11px] text-bark-600 sticky left-0 bg-white font-medium">
                    {isFirst
                      ? "Year 0 (Investment)"
                      : isLast
                      ? `Year ${i} (+ Exit)`
                      : `Year ${i}`}
                  </td>
                  <td
                    className={`px-4 py-2 text-right tabular-nums whitespace-nowrap text-[11px] ${
                      (unlevered[i] ?? 0) < 0
                        ? "text-red-600 font-medium"
                        : "text-bark-600"
                    }`}
                  >
                    {fmtCurrency(unlevered[i] ?? null)}
                  </td>
                  <td
                    className={`px-4 py-2 text-right tabular-nums whitespace-nowrap text-[11px] ${
                      (levered[i] ?? 0) < 0
                        ? "text-red-600 font-medium"
                        : "text-bark-600"
                    }`}
                  >
                    {fmtCurrency(levered[i] ?? null)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary metrics below table */}
      <div className="border-t-2 border-grove-300 bg-grove-50 px-5 py-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
          <MetricCell label="Unlevered IRR" value={fmtPercent(data.irr_unlevered, 2)} />
          <MetricCell label="Levered IRR" value={fmtPercent(data.irr_levered, 2)} />
          <MetricCell label="NPV" value={fmtCurrency(data.npv)} />
          <MetricCell
            label="Unlevered MOIC"
            value={data.equity_multiple_unlevered != null ? fmtMultiple(data.equity_multiple_unlevered) : "--"}
          />
          <MetricCell
            label="Levered MOIC"
            value={data.equity_multiple != null ? fmtMultiple(data.equity_multiple) : "--"}
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Section C: Annual Return Metrics ----------

export function AnnualReturnMetrics({ data }: { data: ReturnMetricsAudit }) {
  const years = data.annual_dscr.length;
  const hasCoc = data.annual_coc.length > 0;
  const hasCocUnlev = (data.annual_coc_unlevered ?? []).length > 0;
  const hasDscr = data.annual_dscr.length > 0;
  const hasDebt = data.loan_amount > 0;

  const avgCoc = hasCoc
    ? data.annual_coc.reduce((a, b) => a + b, 0) / data.annual_coc.length
    : null;
  const avgCocUnlev = hasCocUnlev
    ? data.annual_coc_unlevered.reduce((a, b) => a + b, 0) / data.annual_coc_unlevered.length
    : null;
  const avgDscr = hasDscr
    ? data.annual_dscr.reduce((a, b) => a + b, 0) / data.annual_dscr.length
    : null;
  const minDscr = hasDscr ? Math.min(...data.annual_dscr) : null;

  return (
    <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-grove-100 border-b border-grove-300">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
          Annual Return Metrics
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-grove-50 border-b border-grove-300">
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-bark-400 sticky left-0 bg-grove-50 min-w-[80px]">
                Year
              </th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-bark-400 min-w-[100px]">
                Unlev. CoC
              </th>
              {hasDebt && (
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-bark-400 min-w-[100px]">
                  Lev. CoC
                </th>
              )}
              {hasDebt && (
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-bark-400 min-w-[100px]">
                  DSCR
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: years }, (_, i) => {
              const cocUnlev = hasCocUnlev ? data.annual_coc_unlevered[i] : null;
              const coc = hasCoc ? data.annual_coc[i] : null;
              const dscr = hasDscr ? data.annual_dscr[i] : null;
              return (
                <tr key={i} className="border-b border-grove-100">
                  <td className="px-4 py-2 text-[11px] text-bark-600 sticky left-0 bg-white font-medium">
                    Year {i + 1}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap text-[11px] text-bark-600">
                    {fmtPercent(cocUnlev ?? null, 1)}
                  </td>
                  {hasDebt && (
                    <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap text-[11px] text-bark-600">
                      {fmtPercent(coc, 1)}
                    </td>
                  )}
                  {hasDebt && (
                    <td
                      className={`px-4 py-2 text-right tabular-nums whitespace-nowrap text-[11px] ${
                        dscr != null && dscr < 1.2
                          ? "text-red-600 font-medium"
                          : "text-bark-600"
                      }`}
                    >
                      {fmtMultiple(dscr)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-grove-300 bg-grove-50">
              <td className="px-4 py-2.5 text-[11px] font-semibold text-bark-700 sticky left-0 bg-grove-50">
                Average
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-[11px] font-semibold text-bark-700">
                {fmtPercent(avgCocUnlev, 1)}
              </td>
              {hasDebt && (
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-[11px] font-semibold text-bark-700">
                  {fmtPercent(avgCoc, 1)}
                </td>
              )}
              {hasDebt && (
                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-[11px] font-semibold text-bark-700">
                  {fmtMultiple(avgDscr)}
                </td>
              )}
            </tr>
            {hasDebt && (
              <tr className="bg-grove-50">
                <td className="px-4 py-2.5 text-[11px] font-semibold text-bark-700 sticky left-0 bg-grove-50">
                  Minimum
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-[11px] text-bark-400">
                  --
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-[11px] text-bark-400">
                  --
                </td>
                <td
                  className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap text-[11px] font-semibold ${
                    minDscr != null && minDscr < 1.2
                      ? "text-red-600"
                      : "text-bark-700"
                  }`}
                >
                  {fmtMultiple(minDscr)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ---------- Investment Summary Card ----------

export function InvestmentSummary({ data }: { data: ReturnMetricsAudit }) {
  const hasDebt = data.loan_amount > 0;
  const rows = [
    { label: "Purchase Price", value: fmtCurrency(data.purchase_price) },
    ...(hasDebt ? [
      { label: "Loan Amount", value: fmtCurrency(data.loan_amount) },
      { label: "Equity", value: fmtCurrency(data.equity), bold: true },
    ] : []),
    {
      label: "Going-In Cap Rate",
      value: fmtPercent(data.going_in_cap, 2),
    },
    {
      label: "Avg Unlev. Cash-on-Cash",
      value: fmtPercent(data.avg_cash_on_cash_unlevered, 1),
    },
    ...(hasDebt ? [{
      label: "Avg Lev. Cash-on-Cash",
      value: fmtPercent(data.avg_cash_on_cash, 1),
    }] : []),
    {
      label: "Unlevered MOIC",
      value: data.equity_multiple_unlevered != null ? fmtMultiple(data.equity_multiple_unlevered) : "--",
    },
    ...(hasDebt ? [{
      label: "Levered MOIC",
      value: data.equity_multiple != null ? fmtMultiple(data.equity_multiple) : "--",
    }] : []),
    ...(hasDebt ? [
      {
        label: "Avg DSCR",
        value: fmtMultiple(data.avg_dscr),
      },
      {
        label: "Min DSCR",
        value: fmtMultiple(data.min_dscr),
      },
    ] : []),
  ];

  return (
    <div className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-grove-100 border-b border-grove-300">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-bark-500">
          Investment Summary
        </h3>
      </div>
      <div className="divide-y divide-grove-100">
        {rows.map((r, i) => (
          <div
            key={i}
            className={`flex items-center justify-between px-5 py-2.5 ${
              r.bold ? "bg-grove-50" : ""
            }`}
          >
            <span
              className={`text-[11px] ${
                r.bold ? "font-semibold text-bark-700" : "text-bark-600"
              }`}
            >
              {r.label}
            </span>
            <span
              className={`text-[11px] tabular-nums ${
                r.bold ? "font-semibold text-bark-800" : "text-bark-600"
              }`}
            >
              {r.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
